import Swal from "sweetalert2"
import { showToast } from "utils/swalToast"
import config from "config"

// ==================== DEPENDENCIAS ====================

async function fetchDependencias(sucursalId = null) {
  let url = `${config.api.baseURL}/dependencias-asistencia`
  if (sucursalId) {
    url += `?sucursalId=${sucursalId}`
  }
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener las dependencias")
      }
      return response.json()
    })
    .then((data) => {
      // El backend ahora retorna estructura paginada: {content: [...], totalElements, totalPages, ...}
      return data.content || []
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      return []
    })
}

async function fetchDependenciaById(id) {
  let url = `${config.api.baseURL}/dependencias-asistencia/${id}`
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener la dependencia")
      }
      return response.json()
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      throw error
    })
}

// ==================== TIPOS DE ASISTENTE ====================

async function fetchTiposAsistente(dependenciaId = null) {
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  // Si hay dependenciaId, usar el endpoint sin paginación para dropdown
  if (dependenciaId) {
    let url = `${config.api.baseURL}/tipos-asistente/organizacion/${dependenciaId}`

    return fetch(url, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response?.ok) {
          throw new Error("Error al obtener los tipos de asistente")
        }
        return response.json()
      })
      .then((data) => {
        // Este endpoint devuelve array directo sin paginación
        return data || []
      })
      .catch((error) => {
        showToast({ icon: "error", title: "Error", text: error.message })
        return []
      })
  }

  // Si no hay dependenciaId, usar el endpoint paginado
  let url = `${config.api.baseURL}/tipos-asistente`

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener los tipos de asistente")
      }
      return response.json()
    })
    .then((data) => {
      // El backend retorna estructura paginada: {content: [...], totalElements, totalPages, ...}
      return data.content || []
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      return []
    })
}

// ==================== ASISTENTES ====================

async function fetchAsistentes(params = {}) {
  const queryParams = new URLSearchParams()

  if (params.dependenciaId) queryParams.append("dependenciaId", params.dependenciaId)
  if (params.tipoAsistenteId) queryParams.append("tipoAsistenteId", params.tipoAsistenteId)
  if (params.documentNumber) queryParams.append("documentNumber", params.documentNumber)
  if (params.page !== undefined) queryParams.append("page", params.page)
  if (params.size !== undefined) queryParams.append("size", params.size)

  let url = `${config.api.baseURL}/asistentes`
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`
  }

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener los asistentes")
      }
      return response.json()
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      return { content: [], totalElements: 0, totalPages: 0 }
    })
}

async function fetchAsistenteById(id) {
  let url = `${config.api.baseURL}/asistentes/${id}`
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener el asistente")
      }
      return response.json()
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      throw error
    })
}

async function createAsistente(data) {
  let url = `${config.api.baseURL}/asistentes`
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || "Error al crear el asistente")
        })
      }
      return response.json()
    })
    .then((result) => {
      showToast({ title: "Asistente creado exitosamente", icon: "success" })
      return result
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      throw error
    })
}

async function updateAsistente(id, data) {
  let url = `${config.api.baseURL}/asistentes/${id}`
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "PUT",
    body: JSON.stringify(data),
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || "Error al actualizar el asistente")
        })
      }
      return response.json()
    })
    .then((result) => {
      showToast({ title: "Asistente actualizado exitosamente", icon: "success" })
      return result
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      throw error
    })
}

async function deleteAsistente(id) {
  return Swal.fire({
    title: "¿Está seguro?",
    text: "Esta acción eliminará el asistente permanentemente",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
  }).then((result) => {
    if (result.isConfirmed) {
      let url = `${config.api.baseURL}/asistentes/${id}`
      const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

      return fetch(url, {
        method: "DELETE",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          if (!response?.ok) {
            return response.json().then((error) => {
              throw new Error(error.error || "Error al eliminar el asistente")
            })
          }
          showToast({ title: "Eliminado", text: "El asistente ha sido eliminado", icon: "success" })
          return true
        })
        .catch((error) => {
          showToast({ icon: "error", title: "Error", text: error.message })
          throw error
        })
    }
    return false
  })
}

// ==================== REGISTROS DE ASISTENCIA ====================

async function fetchRegistrosAsistencia(params = {}) {
  const queryParams = new URLSearchParams()

  if (params.dependenciaId) queryParams.append("dependenciaId", params.dependenciaId)
  if (params.tipoAsistenteId) queryParams.append("tipoAsistenteId", params.tipoAsistenteId)
  if (params.asistenteId) queryParams.append("asistenteId", params.asistenteId)
  if (params.fechaInicio) queryParams.append("fechaInicio", params.fechaInicio)
  if (params.fechaFin) queryParams.append("fechaFin", params.fechaFin)
  if (params.page !== undefined) queryParams.append("page", params.page)
  if (params.size !== undefined) queryParams.append("size", params.size)

  let url = `${config.api.baseURL}/reportes-asistencia`
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`
  }

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener los registros de asistencia")
      }
      return response.json()
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      return { content: [], totalElements: 0, totalPages: 0 }
    })
}

async function exportRegistrosAsistencia(params = {}) {
  const queryParams = new URLSearchParams()

  if (params.dependenciaId) queryParams.append("dependenciaId", params.dependenciaId)
  if (params.tipoAsistenteId) queryParams.append("tipoAsistenteId", params.tipoAsistenteId)
  if (params.asistenteId) queryParams.append("asistenteId", params.asistenteId)
  if (params.fechaInicio) queryParams.append("fechaInicio", params.fechaInicio)
  if (params.fechaFin) queryParams.append("fechaFin", params.fechaFin)
  if (params.empresaNombre) queryParams.append("empresaNombre", params.empresaNombre)
  if (params.sucursalNombre) queryParams.append("sucursalNombre", params.sucursalNombre)
  if (params.dependenciaNombre) queryParams.append("dependenciaNombre", params.dependenciaNombre)

  let url = `${config.api.baseURL}/reportes-asistencia/export`
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`
  }

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response?.ok) {
    throw new Error("Error al exportar los registros")
  }

  return response.blob()
}

// ==================== CONFIGURACIONES DE HORARIOS ====================

async function fetchConfiguracionesHorarios(params = {}) {
  const queryParams = new URLSearchParams()

  if (params.dependenciaId) queryParams.append("dependenciaId", params.dependenciaId)
  if (params.tipoAsistenteId) queryParams.append("tipoAsistenteId", params.tipoAsistenteId)
  if (params.diaSemana) queryParams.append("diaSemana", params.diaSemana)
  if (params.activo !== undefined) queryParams.append("activo", params.activo)

  let url = `${config.api.baseURL}/configuraciones-horarios-asistencia`
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`
  }

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener las configuraciones de horarios")
      }
      return response.json()
    })
    .then((data) => {
      // El backend ahora retorna estructura paginada: {content: [...], totalElements, totalPages, ...}
      return data.content || []
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      return []
    })
}

async function createConfiguracionHorario(data) {
  let url = `${config.api.baseURL}/configuraciones-horarios-asistencia`
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || "Error al crear la configuración")
        })
      }
      return response.json()
    })
    .then((result) => {
      showToast({ title: "Configuración creada exitosamente", icon: "success" })
      return result
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      throw error
    })
}

async function updateConfiguracionHorario(id, data) {
  let url = `${config.api.baseURL}/configuraciones-horarios-asistencia/${id}`
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "PUT",
    body: JSON.stringify(data),
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || "Error al actualizar la configuración")
        })
      }
      return response.json()
    })
    .then((result) => {
      showToast({ title: "Configuración actualizada exitosamente", icon: "success" })
      return result
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      throw error
    })
}

async function deleteConfiguracionHorario(id) {
  return Swal.fire({
    title: "¿Está seguro?",
    text: "Esta acción eliminará la configuración de horario",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
  }).then((result) => {
    if (result.isConfirmed) {
      let url = `${config.api.baseURL}/configuraciones-horarios-asistencia/${id}`
      const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

      return fetch(url, {
        method: "DELETE",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          if (!response?.ok) {
            return response.json().then((error) => {
              throw new Error(error.error || "Error al eliminar la configuración")
            })
          }
          showToast({ title: "Eliminado", text: "La configuración ha sido eliminada", icon: "success" })
          return true
        })
        .catch((error) => {
          showToast({ icon: "error", title: "Error", text: error.message })
          throw error
        })
    }
    return false
  })
}

// ==================== SUCURSALES (para dropdown) ====================

async function fetchSucursales() {
  let url = `${config.api.baseURL}/sucursales`
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((response) => {
      if (!response?.ok) {
        throw new Error("Error al obtener las sucursales")
      }
      return response.json()
    })
    .catch((error) => {
      showToast({ icon: "error", title: "Error", text: error.message })
      return []
    })
}

export {
  // Dependencias
  fetchDependencias,
  fetchDependenciaById,
  // Tipos de asistente
  fetchTiposAsistente,
  // Asistentes
  fetchAsistentes,
  fetchAsistenteById,
  createAsistente,
  updateAsistente,
  deleteAsistente,
  // Registros de asistencia
  fetchRegistrosAsistencia,
  exportRegistrosAsistencia,
  // Configuraciones de horarios
  fetchConfiguracionesHorarios,
  createConfiguracionHorario,
  updateConfiguracionHorario,
  deleteConfiguracionHorario,
  // Sucursales
  fetchSucursales,
}
